import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Phone,
  Mail,
  Building,
  Download,
  Upload,
  FileSpreadsheet,
  Zap,
  Flame,
  MessageCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    tipo_cliente: '',
    agente_assegnato: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [newClient, setNewClient] = useState({
    nome: '',
    cognome: '',
    ragione_sociale: '',
    telefono: '',
    email: '',
    tipo_cliente: 'privato',
    citta: '',
    provincia: '',
    codice_fiscale: '',
    fornitore_attuale: '',
    agente_assegnato: '',
    note: ''
  });

  const { hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchUsers();
  }, [filters]);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`${API_URL}/api/clients?${params}`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter(u => ['sub_agent', 'admin', 'super_admin'].includes(u.ruolo)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const handleCreateClient = async () => {
    // Validazione client-side: nome/cognome/telefono/email obbligatori
    if (!newClient.nome?.trim() || !newClient.cognome?.trim()) {
      toast.error('Nome e cognome sono obbligatori');
      return;
    }
    if (!newClient.telefono?.trim()) {
      toast.error('Il telefono e\' obbligatorio');
      return;
    }
    if (!newClient.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newClient.email)) {
      toast.error('L\'email e\' obbligatoria e deve essere valida');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/clients`, newClient);
      toast.success('Cliente creato con successo');
      setShowCreateModal(false);
      setNewClient({
        nome: '',
        cognome: '',
        ragione_sociale: '',
        telefono: '',
        email: '',
        tipo_cliente: 'privato',
        citta: '',
        provincia: '',
        codice_fiscale: '',
        fornitore_attuale: '',
        agente_assegnato: '',
        note: ''
      });
      fetchClients();
    } catch (error) {
      toast.error('Errore nella creazione del cliente');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cliente?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/clients/${clientId}`);
      toast.success('Cliente eliminato');
      fetchClients();
    } catch (error) {
      toast.error('Errore nell\'eliminazione del cliente');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/clients`);
      const blob = new Blob([response.data.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    }
  };

  const handleDownloadTemplate = async (formato) => {
    try {
      const response = await axios.get(`${API_URL}/api/import/template?formato=${formato}`);
      if (formato === 'xlsx') {
        const byteChars = atob(response.data.data);
        const byteNumbers = new Array(byteChars.length).fill(0).map((_, i) => byteChars.charCodeAt(i));
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        a.click();
      } else {
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + response.data.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        a.click();
      }
      toast.success('Template scaricato');
    } catch (error) {
      toast.error('Errore nel download del template');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post(`${API_URL}/api/import/clients`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      setShowImportModal(false);
      setImportFile(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Clienti</h1>
          <p className="text-slate-500 mt-1">{clients.length} clienti totali</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasRole(['super_admin', 'admin']) && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)} data-testid="import-clients-btn">
                <Upload className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Importa</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-clients-btn">
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Esporta</span>
              </Button>
            </>
          )}
          <Button size="sm" onClick={() => setShowCreateModal(true)} data-testid="create-client-btn">
            <Plus className="w-4 h-4 mr-1" />
            Nuovo Cliente
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, telefono, email, POD, PDR..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-clients-input"
                />
              </div>
              <Button type="submit" variant="secondary">Cerca</Button>
            </form>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-slate-100' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtri
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t">
              <Select 
                value={filters.tipo_cliente || "all"} 
                onValueChange={(v) => setFilters({...filters, tipo_cliente: v === "all" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tipo Cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="privato">Privato</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>

              {hasRole(['super_admin', 'admin']) && (
                <Select 
                  value={filters.agente_assegnato || "all"} 
                  onValueChange={(v) => setFilters({...filters, agente_assegnato: v === "all" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clients Table / Mobile Cards */}
      <Card>
        <CardContent className="p-0">
          {/* Desktop / tablet table (>= 640px) */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Contatti</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Citta'</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Fornitore</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Agente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Data</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      Nessun cliente trovato
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr 
                      key={client.id} 
                      className="table-row cursor-pointer"
                      onClick={() => navigate(`/clients/${client.id}`)}
                      data-testid={`client-row-${client.id}`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">
                            {client.tipo_cliente === 'business' && client.ragione_sociale 
                              ? client.ragione_sociale 
                              : `${client.nome} ${client.cognome}`}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            {client.tipo_cliente === 'business' ? (
                              <Building className="w-3 h-3 text-slate-400" />
                            ) : null}
                            <span className="text-sm text-slate-500 capitalize">{client.tipo_cliente}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1 max-w-[140px] sm:max-w-none">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-3 h-3 shrink-0" />
                            <span className="truncate">{client.telefono}</span>
                            <a 
                              href={`https://wa.me/${client.telefono.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="p-1 rounded bg-green-100 hover:bg-green-200 text-green-600 transition-colors shrink-0"
                              title="Apri WhatsApp"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </a>
                          </div>
                          {client.email && (
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <Mail className="w-3 h-3 shrink-0" />
                              <span className="truncate">{client.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="space-y-1">
                          <span className="text-sm text-slate-600">{client.citta || '-'}{client.provincia ? ` (${client.provincia})` : ''}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden lg:table-cell">
                        {client.fornitore_attuale || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden lg:table-cell">
                        {client.agente_nome || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 tabular-nums hidden md:table-cell">
                        {formatDate(client.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}/edit`)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            {hasRole(['super_admin', 'admin']) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteClient(client.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (< 640px) */}
          <div className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
            {clients.length === 0 ? (
              <p className="px-4 py-12 text-center text-slate-500">Nessun cliente trovato</p>
            ) : (
              clients.map((client) => {
                const displayName = client.tipo_cliente === 'business' && client.ragione_sociale
                  ? client.ragione_sociale
                  : `${client.nome} ${client.cognome}`;
                const waNumber = (client.telefono || '').replace(/[^0-9]/g, '');
                return (
                  <div
                    key={client.id}
                    className="px-4 py-3 active:bg-slate-50 dark:active:bg-slate-800 cursor-pointer"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    data-testid={`client-card-${client.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {client.tipo_cliente === 'business' && <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                          <p className="font-medium text-slate-900 dark:text-slate-100 truncate text-sm">{displayName}</p>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 capitalize mt-0.5">
                          {client.tipo_cliente}
                          {client.citta ? ` · ${client.citta}${client.provincia ? ` (${client.provincia})` : ''}` : ''}
                        </p>
                      </div>
                      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`client-card-actions-${client.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> Visualizza
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}/edit`)}>
                              <Edit className="w-4 h-4 mr-2" /> Modifica
                            </DropdownMenuItem>
                            {hasRole(['super_admin', 'admin']) && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteClient(client.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Elimina
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      <Phone className="w-3 h-3 shrink-0 text-slate-400" />
                      <span className="truncate flex-1">{client.telefono}</span>
                      {waNumber && (
                        <a
                          href={`https://wa.me/${waNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 rounded bg-green-100 hover:bg-green-200 text-green-600 shrink-0"
                          title="Apri WhatsApp"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                    {client.email && (
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <Mail className="w-3 h-3 shrink-0" />
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="truncate">{client.fornitore_attuale || client.agente_nome || '-'}</span>
                      <span className="tabular-nums shrink-0">{formatDate(client.created_at)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Client Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Cliente</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={newClient.nome}
                onChange={(e) => setNewClient({...newClient, nome: e.target.value})}
                placeholder="Mario"
              />
            </div>
            <div className="space-y-2">
              <Label>Cognome *</Label>
              <Input
                value={newClient.cognome}
                onChange={(e) => setNewClient({...newClient, cognome: e.target.value})}
                placeholder="Rossi"
              />
            </div>
            <div className="space-y-2">
              <Label>Ragione Sociale</Label>
              <Input
                value={newClient.ragione_sociale}
                onChange={(e) => setNewClient({...newClient, ragione_sociale: e.target.value})}
                placeholder="Azienda Srl"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo Cliente</Label>
              <Select 
                value={newClient.tipo_cliente} 
                onValueChange={(v) => setNewClient({...newClient, tipo_cliente: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="privato">Privato</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Telefono *</Label>
              <Input
                value={newClient.telefono}
                onChange={(e) => setNewClient({...newClient, telefono: e.target.value})}
                placeholder="+39 333 1234567"
                required
                data-testid="client-telefono"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                placeholder="mario.rossi@email.it"
                required
                data-testid="client-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Città</Label>
              <Input
                value={newClient.citta}
                onChange={(e) => setNewClient({...newClient, citta: e.target.value})}
                placeholder="Milano"
              />
            </div>
            <div className="space-y-2">
              <Label>Provincia</Label>
              <Input
                value={newClient.provincia}
                onChange={(e) => setNewClient({...newClient, provincia: e.target.value})}
                placeholder="MI"
                maxLength={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Codice Fiscale</Label>
              <Input
                value={newClient.codice_fiscale}
                onChange={(e) => setNewClient({...newClient, codice_fiscale: e.target.value.toUpperCase()})}
                placeholder="RSSMRA80A01F205X"
              />
            </div>
            <div className="space-y-2">
              <Label>Fornitore Attuale</Label>
              <Input
                value={newClient.fornitore_attuale}
                onChange={(e) => setNewClient({...newClient, fornitore_attuale: e.target.value})}
                placeholder="Enel Energia"
              />
            </div>
            {hasRole(['super_admin', 'admin']) && (
              <div className="space-y-2">
                <Label>Assegna a</Label>
                <Select 
                  value={newClient.agente_assegnato || "none"} 
                  onValueChange={(v) => setNewClient({...newClient, agente_assegnato: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assegnato</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2 space-y-2">
              <Label>Note</Label>
              <textarea
                className="w-full h-24 px-3 py-2 text-sm border rounded-md border-slate-300"
                value={newClient.note}
                onChange={(e) => setNewClient({...newClient, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateClient}
              disabled={!newClient.nome || !newClient.cognome || !newClient.telefono}
              data-testid="submit-new-client-btn"
            >
              Crea Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importa Clienti</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div>
              <p className="text-sm text-slate-600 mb-3">Scarica il template, compilalo con i tuoi contatti e caricalo qui.</p>
              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('xlsx')} data-testid="download-template-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Scarica Template Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('csv')} data-testid="download-template-csv">
                  <Download className="w-4 h-4 mr-2" />
                  Scarica Template CSV
                </Button>
              </div>
            </div>
            <div className="border-t pt-4">
              <Label>Carica file compilato</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-900 file:text-white hover:file:bg-slate-800 cursor-pointer"
                  data-testid="import-file-input"
                />
              </div>
              {importFile && (
                <p className="text-sm text-emerald-600 mt-2">File selezionato: {importFile.name}</p>
              )}
            </div>
            <div className="bg-slate-50 p-3 rounded-lg text-xs text-slate-500 space-y-1">
              <p>I campi obbligatori sono: <strong>Nome, Cognome, Telefono</strong></p>
              <p>I contatti duplicati (stesso nome+cognome+telefono) vengono saltati automaticamente.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportFile(null); }}>Annulla</Button>
            <Button onClick={handleImport} disabled={!importFile || importing} data-testid="submit-import-btn">
              {importing ? 'Importazione...' : 'Importa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
